import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Progress } from "@/components/ui/progress";
import { Upload, Database, Check, AlertCircle, Target, TrendingUp, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

interface ParsedKeyword {
  keyword: string;
  searchIntent: string;
  searchVolume: string;
  competitionLevel: string;
  relevanceScore: number;
}

// Pre-parsed keywords from the uploaded CSV - Extended with 30 new keywords
const csvKeywords: ParsedKeyword[] = [
  // Original keywords
  { keyword: "Smart Toilet", searchIntent: "Commercial/Transactional", searchVolume: "Very High", competitionLevel: "Very High", relevanceScore: 5 },
  { keyword: "Intelligent Toilet", searchIntent: "Commercial/Transactional", searchVolume: "High", competitionLevel: "High", relevanceScore: 5 },
  { keyword: "Bidet Toilet", searchIntent: "Commercial/Transactional", searchVolume: "High", competitionLevel: "High", relevanceScore: 4 },
  { keyword: "Automatic Toilet", searchIntent: "Commercial/Transactional", searchVolume: "Medium", competitionLevel: "Medium", relevanceScore: 4 },
  { keyword: "Electric Bidet", searchIntent: "Commercial/Transactional", searchVolume: "High", competitionLevel: "Medium", relevanceScore: 4 },
  { keyword: "Washlet", searchIntent: "Commercial/Transactional", searchVolume: "Medium", competitionLevel: "Medium", relevanceScore: 4 },
  { keyword: "Japanese Toilet", searchIntent: "Informational/Commercial", searchVolume: "High", competitionLevel: "Medium", relevanceScore: 4 },
  { keyword: "Heated Toilet Seat", searchIntent: "Product/Commercial", searchVolume: "Very High", competitionLevel: "High", relevanceScore: 3 },
  { keyword: "Touchless Toilet", searchIntent: "Product/Commercial", searchVolume: "Medium", competitionLevel: "Medium", relevanceScore: 3 },
  { keyword: "Integrated Bidet Toilet", searchIntent: "Commercial/Product", searchVolume: "Medium", competitionLevel: "Medium", relevanceScore: 5 },
  { keyword: "Self Cleaning Toilet", searchIntent: "Informational/Product", searchVolume: "High", competitionLevel: "Medium", relevanceScore: 3 },
  { keyword: "Tankless Toilet", searchIntent: "Product/Commercial", searchVolume: "Medium", competitionLevel: "Medium", relevanceScore: 3 },
  { keyword: "Smart Bathroom", searchIntent: "Informational/Broad", searchVolume: "High", competitionLevel: "High", relevanceScore: 2 },
  { keyword: "Smart Home Devices", searchIntent: "Informational/Broad", searchVolume: "Very High", competitionLevel: "Very High", relevanceScore: 1 },
  { keyword: "IoT Toilet", searchIntent: "Informational/Technical", searchVolume: "Low", competitionLevel: "Low", relevanceScore: 4 },
  { keyword: "Luxury Toilet", searchIntent: "Informational/Commercial", searchVolume: "Medium", competitionLevel: "Medium", relevanceScore: 3 },
  { keyword: "Electronic Toilet", searchIntent: "Commercial/Transactional", searchVolume: "Medium", competitionLevel: "Medium", relevanceScore: 4 },
  { keyword: "Toilet Technology", searchIntent: "Informational", searchVolume: "Low", competitionLevel: "Low", relevanceScore: 3 },
  { keyword: "Toilet with Dryer", searchIntent: "Product/Commercial", searchVolume: "Medium", competitionLevel: "Medium", relevanceScore: 3 },
  { keyword: "Toilet with Remote", searchIntent: "Product/Commercial", searchVolume: "Low", competitionLevel: "Low", relevanceScore: 3 },
  { keyword: "Hygiene Toilet", searchIntent: "Informational/Product", searchVolume: "Low", competitionLevel: "Low", relevanceScore: 4 },
  { keyword: "Modern Toilet", searchIntent: "Informational/Product", searchVolume: "High", competitionLevel: "Medium", relevanceScore: 2 },
  { keyword: "Best Smart Toilet", searchIntent: "Commercial/Review", searchVolume: "High", competitionLevel: "High", relevanceScore: 5 },
  { keyword: "Affordable Smart Toilet", searchIntent: "Commercial/Price", searchVolume: "Medium", competitionLevel: "Medium", relevanceScore: 4 },
  { keyword: "Smart Toilet Features", searchIntent: "Informational/Comparison", searchVolume: "Medium", competitionLevel: "Medium", relevanceScore: 4 },
  { keyword: "Wall Hung Smart Toilet", searchIntent: "Product/Commercial", searchVolume: "Medium", competitionLevel: "Medium", relevanceScore: 4 },
  { keyword: "Bidet Conversion Kit", searchIntent: "Product/Commercial", searchVolume: "Medium", competitionLevel: "Medium", relevanceScore: 3 },
  { keyword: "Toilet Health Monitor", searchIntent: "Informational/Niche", searchVolume: "Low", competitionLevel: "Low", relevanceScore: 4 },
  { keyword: "Water Saving Toilet", searchIntent: "Informational/Product", searchVolume: "High", competitionLevel: "Medium", relevanceScore: 2 },
  { keyword: "Advanced Toilet", searchIntent: "Informational/Broad", searchVolume: "Low", competitionLevel: "Low", relevanceScore: 3 },
  
  // NEW EXTENDED KEYWORDS (30 additional)
  { keyword: "Bidet Toilet Seat", searchIntent: "Commercial/Product", searchVolume: "Very High", competitionLevel: "High", relevanceScore: 5 },
  { keyword: "Automatic Flushing Toilet", searchIntent: "Commercial/Product", searchVolume: "High", competitionLevel: "Medium", relevanceScore: 4 },
  { keyword: "Smart Bidet", searchIntent: "Commercial/Transactional", searchVolume: "Very High", competitionLevel: "High", relevanceScore: 5 },
  { keyword: "Eco-Friendly Toilet", searchIntent: "Informational/Commercial", searchVolume: "High", competitionLevel: "Medium", relevanceScore: 4 },
  { keyword: "Smart Home Toilet", searchIntent: "Commercial/Product", searchVolume: "High", competitionLevel: "High", relevanceScore: 4 },
  { keyword: "Modern Toilet Design", searchIntent: "Informational/Design", searchVolume: "High", competitionLevel: "Medium", relevanceScore: 4 },
  { keyword: "Smart Toilet Brands", searchIntent: "Commercial/Comparison", searchVolume: "High", competitionLevel: "High", relevanceScore: 5 },
  { keyword: "Toilet With Bidet Function", searchIntent: "Commercial/Product", searchVolume: "High", competitionLevel: "Medium", relevanceScore: 5 },
  { keyword: "Smart Toilet Reviews", searchIntent: "Commercial/Review", searchVolume: "Very High", competitionLevel: "High", relevanceScore: 5 },
  { keyword: "Smart Toilet Installation", searchIntent: "Informational/Guide", searchVolume: "Medium", competitionLevel: "Low", relevanceScore: 5 },
  { keyword: "Smart Toilet Maintenance", searchIntent: "Informational/Guide", searchVolume: "Medium", competitionLevel: "Low", relevanceScore: 4 },
  { keyword: "Smart Toilet Price", searchIntent: "Commercial/Transactional", searchVolume: "High", competitionLevel: "High", relevanceScore: 4 },
  { keyword: "Smart Toilet Comparison", searchIntent: "Commercial/Comparison", searchVolume: "High", competitionLevel: "Medium", relevanceScore: 5 },
  { keyword: "Smart Toilet Technology", searchIntent: "Informational/Technical", searchVolume: "Medium", competitionLevel: "Medium", relevanceScore: 4 },
  { keyword: "Smart Toilet Models", searchIntent: "Commercial/Product", searchVolume: "Medium", competitionLevel: "Medium", relevanceScore: 4 },
  { keyword: "Smart Toilet Accessories", searchIntent: "Commercial/Product", searchVolume: "Medium", competitionLevel: "Low", relevanceScore: 3 },
  { keyword: "Smart Toilet For Elderly", searchIntent: "Commercial/Niche", searchVolume: "High", competitionLevel: "Medium", relevanceScore: 5 },
  { keyword: "Smart Toilet For Disabled", searchIntent: "Commercial/Niche", searchVolume: "Medium", competitionLevel: "Low", relevanceScore: 5 },
  { keyword: "Smart Toilet For Small Bathrooms", searchIntent: "Commercial/Niche", searchVolume: "Medium", competitionLevel: "Low", relevanceScore: 4 },
  { keyword: "Smart Toilet With Air Dryer", searchIntent: "Commercial/Feature", searchVolume: "High", competitionLevel: "Medium", relevanceScore: 4 },
  { keyword: "Smart Toilet With Deodorizer", searchIntent: "Commercial/Feature", searchVolume: "Medium", competitionLevel: "Low", relevanceScore: 4 },
  { keyword: "Smart Toilet With UV Sterilization", searchIntent: "Commercial/Feature", searchVolume: "Medium", competitionLevel: "Low", relevanceScore: 4 },
];

const volumeToNumber = (volume: string): number => {
  switch (volume) {
    case "Very High": return 10000;
    case "High": return 5000;
    case "Medium": return 1000;
    case "Low": return 100;
    default: return 0;
  }
};

const competitionToNumber = (level: string): number => {
  switch (level) {
    case "Very High": return 90;
    case "High": return 70;
    case "Medium": return 50;
    case "Low": return 30;
    default: return 50;
  }
};

export function KeywordImporter() {
  const { user } = useAuth();
  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [imported, setImported] = useState(false);
  const [existingCount, setExistingCount] = useState(0);

  useEffect(() => {
    checkExistingKeywords();
  }, [user]);

  const checkExistingKeywords = async () => {
    if (!user) return;
    
    const { count } = await supabase
      .from('seo_keywords')
      .select('*', { count: 'exact', head: true })
      .eq('agent_id', user.id);
    
    setExistingCount(count || 0);
    if (count && count > 0) {
      setImported(true);
    }
  };

  const importKeywords = async () => {
    if (!user) {
      toast.error("Please log in to import keywords");
      return;
    }

    setImporting(true);
    setProgress(0);

    try {
      const total = csvKeywords.length;
      let successCount = 0;
      let skipCount = 0;

      for (let i = 0; i < csvKeywords.length; i++) {
        const kw = csvKeywords[i];
        
        // Check if keyword already exists
        const { data: existing } = await supabase
          .from('seo_keywords')
          .select('id')
          .eq('agent_id', user.id)
          .eq('keyword', kw.keyword)
          .maybeSingle();

        if (existing) {
          skipCount++;
        } else {
          const { error } = await supabase
            .from('seo_keywords')
            .insert({
              agent_id: user.id,
              keyword: kw.keyword,
              page_url: '/smart-toilets',
              search_volume: volumeToNumber(kw.searchVolume),
              difficulty: competitionToNumber(kw.competitionLevel),
              target_ranking: kw.relevanceScore >= 4 ? 10 : 20,
              status: 'active'
            });

          if (!error) {
            successCount++;
          }
        }

        setProgress(Math.round(((i + 1) / total) * 100));
      }

      setImported(true);
      checkExistingKeywords();
      
      if (skipCount > 0) {
        toast.success(`Imported ${successCount} keywords, skipped ${skipCount} duplicates`);
      } else {
        toast.success(`Successfully imported ${successCount} keywords!`);
      }
    } catch (error: any) {
      console.error('Import error:', error);
      toast.error("Failed to import keywords");
    } finally {
      setImporting(false);
    }
  };

  const getVolumeColor = (volume: string) => {
    switch (volume) {
      case "Very High": return "bg-green-500 text-white";
      case "High": return "bg-blue-500 text-white";
      case "Medium": return "bg-yellow-500 text-white";
      case "Low": return "bg-gray-400 text-white";
      default: return "bg-gray-300";
    }
  };

  const getCompetitionColor = (level: string) => {
    switch (level) {
      case "Very High": return "text-red-600 bg-red-50";
      case "High": return "text-orange-600 bg-orange-50";
      case "Medium": return "text-yellow-600 bg-yellow-50";
      case "Low": return "text-green-600 bg-green-50";
      default: return "text-gray-600 bg-gray-50";
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Target className="h-5 w-5" />
              Keyword Importer
            </CardTitle>
            <CardDescription>
              Import 30 SEO keywords from the uploaded CSV to your database for tracking
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            {imported && (
              <Badge variant="secondary" className="bg-green-100 text-green-700">
                <Check className="h-3 w-3 mr-1" />
                {existingCount} Keywords in DB
              </Badge>
            )}
            <Button
              onClick={importKeywords}
              disabled={importing}
              variant={imported ? "outline" : "default"}
            >
              {importing ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Importing...
                </>
              ) : imported ? (
                <>
                  <Database className="h-4 w-4 mr-2" />
                  Re-import Keywords
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-2" />
                  Import to Database
                </>
              )}
            </Button>
          </div>
        </div>
        {importing && (
          <div className="mt-4">
            <Progress value={progress} className="h-2" />
            <p className="text-sm text-muted-foreground mt-1">{progress}% complete</p>
          </div>
        )}
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto max-h-[400px]">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Keyword</TableHead>
                <TableHead>Search Intent</TableHead>
                <TableHead>Volume</TableHead>
                <TableHead>Competition</TableHead>
                <TableHead>Relevance</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {csvKeywords.map((kw, index) => (
                <TableRow key={index}>
                  <TableCell className="font-medium">{kw.keyword}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className="text-xs">
                      {kw.searchIntent}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge className={getVolumeColor(kw.searchVolume)}>
                      {kw.searchVolume}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant="secondary" className={getCompetitionColor(kw.competitionLevel)}>
                      {kw.competitionLevel}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <TrendingUp
                          key={i}
                          className={`h-3 w-3 ${
                            i < kw.relevanceScore ? 'text-primary' : 'text-muted'
                          }`}
                        />
                      ))}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}

export default KeywordImporter;
